/**
 * cartDrawer.js
 *
 * @version 1.0.0
 */

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */
const SEL = {
	drawer: document.querySelector('[data-cart-drawer]'),
	drawerForm: document.querySelector('[data-cart-form]'),
	threshold: document.querySelector('[data-cart-threshold]'),
	drawerItems: document.querySelector('[data-cart-items]'),
	drawerFooter: document.querySelector('[data-cart-footer]'),
	qtyInput: '[data-quantity-input]',
	cartCount: document.querySelectorAll('[data-cart-count]'),
	cartItemRemove: '[data-cart-remove]',
	cartItemTemplate: document.querySelector('[data-cart-template]'),
	error: '[data-product-error]'
};

/* ----------------------------------------------------------------------- */
/*  Gift-note helpers                                                      */
/* ----------------------------------------------------------------------- */

/** Prefix used for all gift-note cart attribute keys. */
const GIFT_NOTE_PREFIX = 'Gift Message for ';

/** Build the cart-attribute key for a given product title. Strips both
 *  ASCII and curly apostrophes to match the Liquid `replace` filters in
 *  product-form.liquid, cart-drawer.liquid, and order-confirmation.liquid. */
const giftNoteKey = (productHandle) => `${GIFT_NOTE_PREFIX}${productHandle}`;

/**
 * Collapse whitespace and truncate a string for inline preview.
 * Matches the Liquid `strip_newlines | truncate: 80, '…'` used by
 * cart-drawer.liquid so both render paths look identical.
 */
function truncatePreview(str, max = 80, ellipsis = '…') {
	const cleaned = String(str).replace(/\s+/g, ' ').trim();
	if (cleaned.length <= max) return cleaned;
	return cleaned.slice(0, Math.max(0, max - ellipsis.length)) + ellipsis;
}

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

const ENDPOINT = {
	CART: '/cart.json',
	ADD: '/cart/add.js',
	CHANGE: '/cart/change.js',
	UPDATE: '/cart/update.js'
};

/** Decode HTML entities that Shopify includes in /cart.json strings. */
const _decodeEl = document.createElement('textarea');
function decodeHTML(str) {
	_decodeEl.innerHTML = str;
	return _decodeEl.value;
}

const formatMoney = cents =>
	(cents / 100).toLocaleString('en-AU', {
		style: 'currency',
		currency: 'AUD',
		minimumFractionDigits: 2
	});

async function fetchJSON(url, opts = {}) {
	const defaults = {
		credentials: 'same-origin',
		cache: 'no-store',
		headers: {
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest'
		}
	};
	const res = await fetch(url, { ...defaults, ...opts });
	const data = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(data?.message || `${res.status} ${res.statusText}`);
	return data;
}

const getCart = () => fetchJSON(ENDPOINT.CART);

const changeCart = (key, qty = 1) =>
	fetchJSON(ENDPOINT.CHANGE, {
		method: 'POST',
		body: JSON.stringify({ id: key, quantity: qty })
	});

/**
 * Merge a set of attributes onto the cart. Pass a key with value ""
 * to delete that attribute.
 *
 * @param {Object<string,string>} attributes
 */
const updateCartAttributes = (attributes) =>
	fetchJSON(ENDPOINT.UPDATE, {
		method: 'POST',
		body: JSON.stringify({ attributes })
	});

/* ----------------------------------------------------------------------- */
/*  Card helpers                                                           */
/* ----------------------------------------------------------------------- */

/** Private property on a card line naming the box it goes in (set by cardOption.js). */
const CARD_FOR_PROPERTY = '_cardFor';

/** Private property on a box line holding the same id, pairing the box with its card. */
const CARD_ID_PROPERTY = '_cardId';

/**
 * Whether a line is a card added with a box. Its quantity is fixed at one card per box, so it gets no quantity selector.
 *
 * @param {Object} item  A /cart.json line.
 * @returns {boolean}
 */
function isCardWithBox(item) {
	return Boolean(item.properties && item.properties[CARD_FOR_PROPERTY]);
}

/**
 * The details the customer wrote on a line, such as a card message, leaving out private (underscore) properties and blanks.
 *
 * @param {Object} item  A /cart.json line.
 * @returns {Array<[string, string]>}
 */
function visibleProperties(item) {
	if (!item.properties) {
		return [];
	}
	return Object.entries(item.properties).filter(([key, value]) => {
		return !key.startsWith('_') && value !== null && String(value).trim() !== '';
	});
}

/**
 * The pairing id a box line carries, or null for a box without a card (and any other line).
 *
 * @param {Object} item  A /cart.json line.
 * @returns {string|null}
 */
function boxCardId(item) {
	if (!item.properties || !item.properties[CARD_ID_PROPERTY]) {
		return null;
	}
	return String(item.properties[CARD_ID_PROPERTY]);
}

/**
 * Each box's card, keyed by the pairing id the two lines share.
 *
 * @param {Object} cart  /cart.json
 * @returns {Object<string, Object>}
 */
function cardsByBox(cart) {
	const cards = {};
	cart.items.forEach((item) => {
		if (isCardWithBox(item)) {
			cards[String(item.properties[CARD_FOR_PROPERTY])] = item;
		}
	});
	return cards;
}

/**
 * Whether a card's box is still in the cart, in which case the card shows inside that box.
 *
 * @param {Object} item  A card line.
 * @param {Set<string>} boxIds  The pairing ids of the boxes in the cart.
 * @returns {boolean}
 */
function hasBoxInCart(item, boxIds) {
	return boxIds.has(String(item.properties[CARD_FOR_PROPERTY]));
}

/**
 * The pairing ids of every box in the cart that carries a card.
 *
 * @param {Object} cart  /cart.json
 * @returns {Set<string>}
 */
function boxCardIds(cart) {
	const ids = new Set();
	cart.items.forEach((item) => {
		const id = boxCardId(item);
		if (id) {
			ids.add(id);
		}
	});
	return ids;
}

/**
 * The quantity changes that take out cards whose box has left the cart: a card stays only while
 * the box line sharing its id is still there.
 *
 * @param {Object} cart  /cart.json
 * @returns {Object<string, number>} Line key to its new quantity; empty when nothing needs to change.
 */
function cardsWithoutBoxes(cart) {
	const boxIds = boxCardIds(cart);
	const updates = {};
	cart.items.forEach((item) => {
		if (isCardWithBox(item) && !hasBoxInCart(item, boxIds)) {
			updates[item.key] = 0;
		}
	});
	return updates;
}

/**
 * Takes out any cards whose box has left the cart.
 *
 * @param {Object} cart  The cart as it now stands.
 * @returns {Promise<boolean>} True when cards were taken out.
 */
async function removeCardsWithoutBoxes(cart) {
	const updates = cardsWithoutBoxes(cart);
	if (Object.keys(updates).length === 0) {
		return false;
	}
	await fetchJSON(ENDPOINT.UPDATE, {
		method: 'POST',
		body: JSON.stringify({
			updates: updates,
		}),
	});
	return true;
}

function showItemError(itemNode, message) {
	const box = itemNode?.querySelector(SEL.error);
	if (!box) return;
	box.textContent = message;
	box.classList.remove('hide');
}

function clearItemError(itemNode) {
	const box = itemNode?.querySelector(SEL.error);
	if (!box) return;
	box.textContent = '';
	box.classList.add('hide');
}

function setItemLoading(itemNode, busy) {
	if (!itemNode) return;
	itemNode.querySelector('.price')?.classList.toggle('hidden', busy);
	itemNode.querySelector('.loader')?.classList.toggle('hide', !busy);
}

/* ----------------------------------------------------------------------- */
/*  Render helpers                                                         */
/* ----------------------------------------------------------------------- */

function renderCount(cart) {
	if (!SEL.cartCount) return;
	SEL.cartCount.forEach(elem => {
		elem.textContent = cart.item_count;
		elem.classList.toggle('hide', cart.item_count === 0);
	});
}

function renderThreshold(cart) {
	if (!SEL.threshold) return;
	const threshold = parseInt(SEL.threshold.dataset.threshold, 10);
	const bar = SEL.threshold.querySelector('progress');
	const msg = SEL.threshold.querySelector('.drawer__message');
	bar.max = threshold;
	bar.value = Math.min(cart.total_price, threshold);
	msg.innerHTML =
		cart.total_price >= threshold
			? `<strong>Your order ships for free/discounted
					<a href="https://lastst.com.au/pages/shipping-and-delivery">(T&Cs Apply)</a>
				</strong>`
			: `You're <strong>${formatMoney(threshold - cart.total_price)}</strong> away from free/discounted shipping*. <a href="https://lastst.com.au/pages/shipping-and-delivery">(T&Cs Apply)</a>`;
}

function renderBody(cart) {
	if (!SEL.drawer || !SEL.drawerForm) return;
	SEL.drawer.classList.toggle('is-empty', cart.item_count === 0);
	SEL.drawerForm.classList.toggle('hide', cart.item_count === 0);
}

/**
 * Build a single cart-item DOM node from its /cart.json representation.
 *
 * @param {Object} item
 * @param {number} lineIndex
 * @param {Object} cartAttributes  Full cart.attributes object; used to look
 *                                 up any gift-note keyed by product title.
 */
function renderCartItem(item, lineIndex, cartAttributes = {}, card = null) {
	if (!SEL.cartItemTemplate) return null;

	const frag = SEL.cartItemTemplate.content.cloneNode(true);
	const node = frag.firstElementChild;

	/* Pick & Mix — bundle lines have a fixed quantity. Detect by the
	   merged line's product handle (set by the cart transform), with
	   the _bundleID line property as a fallback for any component
	   lines that haven't been merged. Mirrors cart-drawer.liquid. */
	const isPickAndMix =
		item.handle === 'pick-and-mix' ||
		Boolean(item.properties && item.properties._bundleID);

	/* A box with its card, and a card added with a box, are one of each, so their quantity is fixed too. */
	const fixedQuantity = isPickAndMix || isCardWithBox(item) || Boolean(card);

	// Media
	node.querySelector('.cart-item__link').href = item.url;
	const img = node.querySelector('.cart-item__image');
	img.src = item.image;
	img.alt = item.featured_image?.alt || decodeHTML(item.title);

	// Title — item.product_title (just the product name, no variant)
	const name = node.querySelector('.cart-item__name');
	name.textContent = decodeHTML(item.product_title);
	name.href = item.url;

	// Price
	const priceOld = node.querySelector('.cart-item__old-price');
	const priceNow = node.querySelector('.cart-item__current-price');
	const discounted = item.original_line_price !== item.final_line_price;
	if (discounted) {
		priceOld.hidden = false;
		priceOld.textContent = formatMoney(item.original_line_price);
		priceNow.textContent = formatMoney(item.final_line_price);
	} else {
		priceOld.hidden = true;
		priceNow.textContent = formatMoney(item.original_line_price);
	}

	// Variants
	const variantsDl = node.querySelector('.cart-item__variants');

	if (!item.product_has_only_default_variant && Array.isArray(item.options_with_values)) {
		variantsDl.replaceChildren(
			...item.options_with_values.map(opt => mkVariant(`${decodeHTML(opt.name)}:`, decodeHTML(opt.value)))
		);
	} else {
		variantsDl.remove();
	}

	// Gift note — look up by product title in cart.attributes.
	// Uses textContent (not innerHTML) because the value is user-entered.
	const productTitle = decodeHTML(item.product_title || '');
	const giftMessage = cartAttributes[giftNoteKey(item.handle)];
	if (giftMessage) {
		const propsDl = document.createElement('dl');
		propsDl.className = 'cart-item__properties';
		propsDl.appendChild(mkProperty('Gift Message:', truncatePreview(giftMessage)));
		const footer = node.querySelector('.cart-item__footer');
		if (footer?.parentNode) {
			footer.parentNode.insertBefore(propsDl, footer);
		} else {
			node.querySelector('.cart-item__details')?.appendChild(propsDl);
		}
	}

	// Line details the customer wrote, such as a card message. textContent keeps them as plain text.
	const details = visibleProperties(item);
	if (details.length > 0) {
		const detailsDl = document.createElement('dl');
		detailsDl.className = 'cart-item__properties';
		details.forEach(([key, value]) => {
			detailsDl.appendChild(mkProperty(`${decodeHTML(key)}:`, decodeHTML(String(value))));
		});
		const footer = node.querySelector('.cart-item__footer');
		footer.parentNode.insertBefore(detailsDl, footer);
	}

	// The box's card, shown inside the box line with its message and its own remove link.
	if (card) {
		const footer = node.querySelector('.cart-item__footer');
		footer.parentNode.insertBefore(mkCardRow(card), footer);
	}

	// Quantity: hidden for Pick & Mix bundles and for boxes and cards that go together.
	if (fixedQuantity) {
		const qtyWrap = node.querySelector('[data-quantity]');
		if (qtyWrap) qtyWrap.style.display = 'none';
	} else {
		const qtyInput = node.querySelector(SEL.qtyInput);
		qtyInput.value = item.quantity;
		qtyInput.min = item.quantity_rule?.min ?? 0;
		if (item.quantity_rule?.max) qtyInput.max = item.quantity_rule.max;
	}

	// Remove
	const removeBtn = node.querySelector(SEL.cartItemRemove);
	removeBtn.dataset.variantId = item.key;
	removeBtn.setAttribute('aria-label', `Remove ${decodeHTML(item.title)}`);

	node.dataset.variantId = item.key;
	return node;
}

/** Create a variant detail div. */
function mkVariant(label, value) {
	const div = document.createElement('div');
	div.className = 'cart-item__variant';
	div.innerHTML = `<dt>${label}</dt><dd>${value}</dd>`;
	return div;
}

/**
 * Create a property detail div using textContent so the value cannot
 * be interpreted as HTML (user-entered content safety). CSS cuts a long
 * value, such as a card message, to two lines with an ellipsis.
 */
function mkProperty(label, value) {
	const div = document.createElement('div');
	div.className = 'cart-item__property';
	const dt = document.createElement('dt');
	dt.textContent = label;
	const dd = document.createElement('dd');
	dd.textContent = value;
	div.append(dt, dd);
	return div;
}

/**
 * The card row inside its box line: the card and its price, the message (or that it is blank), and
 * a link to take the card out. Text nodes keep the customer's message as plain text.
 *
 * @param {Object} card  The card's /cart.json line.
 * @returns {HTMLElement}
 */
function mkCardRow(card) {
	const row = document.createElement('div');
	row.className = 'cart-item__card';

	const head = document.createElement('div');
	head.className = 'cart-item__card-head';
	const name = document.createElement('span');
	name.textContent = `+ ${decodeHTML(card.product_title || card.title)}`;
	const price = document.createElement('span');
	price.textContent = formatMoney(card.final_line_price);
	head.append(name, price);

	// Plain text: line breaks show as spaces, and CSS cuts a long message to two lines with an ellipsis.
	const message = document.createElement('p');
	message.className = 'cart-item__card-message';
	const text = card.properties && card.properties.Message ? decodeHTML(String(card.properties.Message)) : '';
	message.textContent = text || 'Blank card (no message)';

	const remove = document.createElement('button');
	remove.className = 'cart-item__card-remove';
	remove.type = 'button';
	remove.dataset.cardRemove = '';
	remove.dataset.lineKey = card.key;
	remove.textContent = 'Remove card';

	row.append(head, message, remove);
	return row;
}

function renderItems(cart) {
	if (!SEL.drawerItems) return;
	const frag = document.createDocumentFragment();
	const attrs = cart.attributes || {};
	const boxIds = boxCardIds(cart);
	const cards = cardsByBox(cart);
	cart.items.forEach((item, index) => {
		/* A card that came with a box shows inside that box, not as a line of its own. */
		if (isCardWithBox(item) && hasBoxInCart(item, boxIds)) {
			return;
		}
		const boxId = boxCardId(item);
		const card = boxId ? cards[boxId] || null : null;
		const li = renderCartItem(item, index + 1, attrs, card);
		if (li) frag.appendChild(li);
	});
	SEL.drawerItems.replaceChildren(frag);
}

function renderFooter(cart) {
	if (!SEL.drawerFooter) return;
	SEL.drawerFooter.querySelector('.totals__value').textContent = formatMoney(cart.total_price);
	SEL.drawerFooter.querySelector('.btn').disabled = false;
}

function renderCart(cart) {
	renderCount(cart);
	renderThreshold(cart);
	renderBody(cart);
	renderItems(cart);
	renderFooter(cart);
}

/* ----------------------------------------------------------------------- */
/*  Cart fetch + UI update                                                 */
/* ----------------------------------------------------------------------- */

let busy = false;

async function updateUI() {
	while (busy) {
		await new Promise(r => setTimeout(r, 50));
	}
	busy = true;
	try {
		const cart = await getCart();
		renderCart(cart);
		return cart;
	} catch (err) {
		console.error('Cart refresh failed:', err);
		throw err;
	} finally {
		busy = false;
	}
}

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */

/**
 * Identify "line is gone" errors from /cart/change.js so we can recover
 * silently on stale clicks rather than surfacing a confusing 400 to the user.
 */
function isStaleLineError(err) {
	const m = String(err && err.message || '').toLowerCase();
	return m.includes('no valid id or line parameter')
		|| m.includes('cart line') && m.includes('not found')
		|| m.includes('400');
}

async function handleRemove(e) {
	const removeBtn = e.target.closest('.cart-item__remove');
	if (!removeBtn) return;

	/* Guard against rapid duplicate clicks: once a remove is in-flight we
	   ignore further clicks on the same button. Without this, two quick
	   clicks queue two /cart/change.js calls with the same line key —
	   the second arrives after the line is already gone and Shopify
	   responds 400 "no valid id or line parameter". */
	if (removeBtn.dataset.loading === 'true') return;
	removeBtn.dataset.loading = 'true';
	removeBtn.disabled = true;

	const cartItem = removeBtn.closest('.cart-item');
	clearItemError(cartItem);
	setItemLoading(cartItem, true);

	try {
		const key = removeBtn.dataset.variantId;
		if (!key) {
			throw new Error('Could not identify the item to remove. Please refresh and try again.');
		}

		const cart = await getCart();
		const item = cart.items.find(i => i.key === key);

		/* If the line is already gone (e.g. because a prior click removed
		   it), don't bother calling change.js — just refresh the UI. */
		if (!item) {
			await updateUI();
			return;
		}

		const updatedCart = await changeCart(key, 0);

		/* A box's cards leave with it. */
		await removeCardsWithoutBoxes(updatedCart);

		/* Clean up any gift-note cart attribute tied to this product.
		   Setting the value to "" deletes the attribute in Shopify. */
		if (cart.attributes) {
			const gkey = giftNoteKey(item.handle);
			if (cart.attributes[gkey]) {
				await updateCartAttributes({ [gkey]: '' });
			}
		}

		await updateUI();
	} catch (err) {
		console.error('Remove from cart failed:', err);

		/* For stale-line errors the cart UI is what's wrong, not the user's
		   input. Refresh silently rather than surfacing the raw API message. */
		if (isStaleLineError(err)) {
			try { await updateUI(); } catch (_) { /* swallow */ }
		} else {
			showItemError(cartItem, 'Could not remove item. Please try again.');
		}
	} finally {
		setItemLoading(cartItem, false);
		/* removeBtn may now be detached after updateUI re-renders the list;
		   resetting the flag is harmless in that case. */
		removeBtn.dataset.loading = 'false';
		removeBtn.disabled = false;
	}
}

/**
 * Takes a card out of its box when the customer presses "Remove card". The box stays in the cart.
 *
 * @param {MouseEvent} e
 */
async function handleCardRemove(e) {
	const button = e.target.closest('[data-card-remove]');
	if (!button) {
		return;
	}
	if (button.dataset.loading === 'true') {
		return;
	}
	button.dataset.loading = 'true';
	button.disabled = true;

	const cartItem = button.closest('.cart-item');
	clearItemError(cartItem);
	try {
		await changeCart(button.dataset.lineKey, 0);
		await updateUI();
	} catch (err) {
		console.error('Remove card failed:', err);
		if (isStaleLineError(err)) {
			try { await updateUI(); } catch (_) { /* swallow */ }
		} else {
			showItemError(cartItem, 'Could not remove the card. Please try again.');
		}
	} finally {
		button.dataset.loading = 'false';
		button.disabled = false;
	}
}

const handleQty = (() => {
	let timer;
	return async function (e) {
		const input = e.target.closest('.quantity__input');
		if (!input) return;
		if (!input.closest('.drawer')) return;

		clearTimeout(timer);
		timer = setTimeout(async () => {
			const cartItem = input.closest('.cart-item');
			const key = cartItem?.dataset.variantId;
			const qty = Math.max(0, parseInt(input.value, 10) || 0);

			if (!key) return;

			clearItemError(cartItem);
			setItemLoading(cartItem, true);

			try {
				const updatedCart = await changeCart(key, qty);
				/* Fewer boxes means fewer cards: one card per box. */
				await removeCardsWithoutBoxes(updatedCart);
				await updateUI();
			} catch (err) {
				console.error('Quantity update failed:', err);
				if (isStaleLineError(err)) {
					try { await updateUI(); } catch (_) { /* swallow */ }
				} else {
					showItemError(cartItem, 'Could not update quantity. Please try again.');
				}
			} finally {
				setItemLoading(cartItem, false);
			}
		}, 300);
	};
})();

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

function initCartDrawer() {
	const root = SEL.drawer;
	if (!root) return () => { };

	/*
	 * Do NOT call updateUI() here. The server-rendered Liquid HTML is
	 * already correct and includes item_components data that /cart.json
	 * does not return. updateUI() is called after mutations only.
	 */

	SEL.drawer?.addEventListener('click', handleRemove);
	SEL.drawer?.addEventListener('click', handleCardRemove);
	document.addEventListener('change', handleQty, true);

	/*
	 * The cart page removes a line with a plain link, so a box removed there
	 * can leave its cards behind. The drawer is flagged whenever the cart holds
	 * cards that came with a box; check once and reload if any were taken out.
	 */
	if (root.hasAttribute('data-card-check')) {
		getCart()
			.then(removeCardsWithoutBoxes)
			.then((removed) => {
				if (removed) {
					window.location.reload();
				}
			})
			.catch((err) => {
				console.error('Card check failed:', err);
			});
	}
}

export { initCartDrawer, updateUI };
